import { readEvent, emit } from './eg-analytics-core.js';

/**
 * Storefront analytics — DOM wiring (U22 / KTD11). Wires declared events to
 * the sink(s); logic lives in eg-analytics-core.js (tested).
 *
 * Two triggers:
 *   click  — any [data-eg-event] element emits on click (quiz-launch, Kit CTA…)
 *   onload — any [data-eg-event][data-eg-event-onload] emits once on load
 *            (quiz result-page landings)
 *
 * A third source (2026-09-02, PostHog): JS modules with no declaring element
 * (the configurator, the ladder, capture) cannot use `data-eg-event` — there
 * is no element to read it off at the moment the event happens. Those modules
 * dispatch a DOM CustomEvent `eg:track` on `document` with
 * `detail: { name, params }` instead; listening for it here keeps them out of
 * the module-import-map graph (theme/tests/module-import-map.test.js) — no
 * new import, just an event they already know how to dispatch.
 *
 * The sink pushes to gtag AND dataLayer AND posthog.capture — all three are
 * optional, store-level-or-snippet config; until one is present the emit is a
 * safe no-op — nothing to configure in the theme.
 */

function sink(name, params) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', name, params);
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: name, ...params });
  if (typeof window.posthog?.capture === 'function') {
    window.posthog.capture(name, params);
  }
}

function wire() {
  // Click-triggered events (delegated, so it covers elements added after load).
  document.addEventListener('click', (event) => {
    const el = event.target.closest('[data-eg-event]');
    if (!el || el.hasAttribute('data-eg-event-onload')) return;
    const ev = readEvent(el.dataset);
    if (ev) emit(sink, ev.name, ev.params);
  });

  // Load-triggered events (result-page landings): emit once each.
  document.querySelectorAll('[data-eg-event][data-eg-event-onload]').forEach((el) => {
    const ev = readEvent(el.dataset);
    if (ev) emit(sink, ev.name, ev.params);
  });

  // JS-module-triggered events (no declaring element — the configurator, the
  // ladder, capture forms). FAILS SILENT: a malformed detail must not throw
  // inside a listener that runs on the configurator's own commit path.
  document.addEventListener('eg:track', (event) => {
    try {
      const detail = event.detail || {};
      if (typeof detail.name === 'string' && detail.name !== '') {
        emit(sink, detail.name, detail.params || {});
      }
    } catch {
      /* analytics must never break the storefront */
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', wire);
} else {
  wire();
}
